import React, { useState, useEffect, useCallback } from 'react';
import api, { API_BASE_URL } from '../services/api';
import { useDebounce } from '../hooks/useDebounce';
import { Calendar, DollarSign, Search, ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight } from 'lucide-react';

const getProofImageUrl = (proofImage) => {
  if (!proofImage) return null;
  if (proofImage.startsWith('http')) return proofImage;
  return `${API_BASE_URL}${proofImage}`;
};

const fmt = (d) => d.toISOString().split('T')[0];

const getDateRange = (dateFilter) => {
  const today = new Date();
  if (dateFilter === 'today') {
    const t = fmt(today);
    return { date_from: t, date_to: t };
  }
  if (dateFilter === 'week') {
    const weekAgo = new Date(today.getTime() - 7 * 24 * 60 * 60 * 1000);
    return { date_from: fmt(weekAgo), date_to: fmt(today) };
  }
  if (dateFilter === 'month') {
    const firstOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
    return { date_from: fmt(firstOfMonth), date_to: fmt(today) };
  }
  return {};
};

const PAGE_SIZE_OPTIONS = [25, 50, 100];

const CollectionHistory = () => {
  const [collections, setCollections] = useState([]);
  const [loading, setLoading] = useState(true);

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [pageSize, setPageSize] = useState(50);
  const [totalAmount, setTotalAmount] = useState(0);

  // Filters
  const [searchTerm, setSearchTerm] = useState('');
  const [methodFilter, setMethodFilter] = useState('all');
  const [dateFilter, setDateFilter] = useState('all');

  const debouncedSearch = useDebounce(searchTerm, 400);

  const totalPages = Math.ceil(totalCount / pageSize) || 1;
  const startItem = totalCount > 0 ? (currentPage - 1) * pageSize + 1 : 0;
  const endItem = Math.min(currentPage * pageSize, totalCount);

  const fetchCollections = useCallback(async (page = 1) => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.set('page', page);
      params.set('page_size', pageSize);
      if (debouncedSearch) params.set('search', debouncedSearch);
      if (methodFilter !== 'all') params.set('payment_method', methodFilter);
      const range = getDateRange(dateFilter);
      if (range.date_from) params.set('date_from', range.date_from);
      if (range.date_to) params.set('date_to', range.date_to);

      const res = await api.get(`/collections/?${params.toString()}`);
      setCollections(res.data.results || []);
      setTotalCount(res.data.count || 0);
      setTotalAmount(res.data.total_amount || 0);
      setCurrentPage(page);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [debouncedSearch, methodFilter, dateFilter, pageSize]);

  // Reset to page 1 when filters change
  useEffect(() => {
    fetchCollections(1);
  }, [fetchCollections]);

  const goToPage = (page) => {
    if (page >= 1 && page <= totalPages) fetchCollections(page);
  };

  return (
    <div className="p-4 sm:p-6 bg-gray-50 dark:bg-gray-900 min-h-screen">
      {/* Stats Card */}
      <div className="bg-white dark:bg-gray-800 p-4 sm:p-6 rounded-xl shadow-sm border-l-4 border-blue-500 mb-4 sm:mb-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <p className="text-gray-500 dark:text-gray-400 text-sm uppercase font-bold">Total Collected (Filtered)</p>
          <h3 className="text-3xl font-bold text-gray-900 dark:text-gray-100 mt-1">PKR {totalAmount.toLocaleString()}</h3>
          <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">{totalCount} record{totalCount !== 1 ? 's' : ''}</p>
        </div>
        <div className="p-3 bg-blue-50 dark:bg-blue-900/30 rounded-full">
          <DollarSign className="w-8 h-8 text-blue-600 dark:text-blue-400" />
        </div>
      </div>

      {/* Filters Bar */}
      <div className="bg-white dark:bg-gray-800 p-4 rounded-xl shadow-sm mb-6 flex flex-col md:flex-row gap-4 items-center justify-between">
        <div className="flex gap-4 w-full md:w-auto">
          {/* Search */}
          <div className="relative flex-1 md:w-64">
            <Search className="absolute left-3 top-2.5 text-gray-400 w-4 h-4" />
            <input
              type="text"
              placeholder="Search customer..."
              className="w-full pl-9 pr-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 outline-none bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>

          {/* Method Filter */}
          <select
            className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 outline-none bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
            value={methodFilter}
            onChange={(e) => setMethodFilter(e.target.value)}
          >
            <option value="all">All Methods</option>
            <option value="cash">Cash</option>
            <option value="jazzcash">JazzCash</option>
            <option value="easypaisa">EasyPaisa</option>
            <option value="bank">Bank Transfer</option>
          </select>
        </div>

        {/* Date Filter */}
        <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-300">
          <Calendar size={16} />
          <select
            className="border border-gray-300 dark:border-gray-600 rounded-lg px-2 py-1.5 outline-none bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
            value={dateFilter}
            onChange={(e) => setDateFilter(e.target.value)}
          >
            <option value="all">All Time</option>
            <option value="today">Today</option>
            <option value="week">Last 7 Days</option>
            <option value="month">This Month</option>
          </select>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-left">
            <thead className="bg-gray-50 dark:bg-gray-700/50 text-gray-600 dark:text-gray-300 text-xs uppercase">
              <tr>
                <th className="p-4">Date & Time</th>
                <th className="p-4">Customer</th>
                <th className="p-4">Method</th>
                <th className="p-4">TX ID</th>
                <th className="p-4">Proof</th>
                <th className="p-4 text-right">Amount</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
              {loading ? (
                <tr><td colSpan="6" className="p-8 text-center text-gray-500 dark:text-gray-400">Loading...</td></tr>
              ) : collections.length === 0 ? (
                <tr><td colSpan="6" className="p-8 text-center text-gray-500 dark:text-gray-400">No records match your filters.</td></tr>
              ) : (
                collections.map((c) => (
                  <tr key={c.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/30 transition-colors">
                    <td className="p-4 text-sm text-gray-600 dark:text-gray-300">
                      {new Date(c.collected_at).toLocaleDateString()}
                      <span className="text-gray-400 dark:text-gray-500 text-xs ml-2">
                        {new Date(c.collected_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                      </span>
                    </td>
                    <td className="p-4 font-bold text-gray-900 dark:text-gray-100">{c.customer_username}</td>
                    <td className="p-4">
                      <span className={`px-2 py-1 rounded-full text-xs font-bold capitalize ${
                        c.payment_method === 'cash' ? 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300' :
                        c.payment_method === 'jazzcash' ? 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300' :
                        c.payment_method === 'easypaisa' ? 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300' :
                        'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300'
                      }`}>
                        {c.payment_method.replace('_', ' ')}
                      </span>
                    </td>
                    <td className="p-4 text-sm text-gray-500 dark:text-gray-400 font-mono">
                      {c.transaction_id || '-'}
                    </td>
                    <td className="p-4">
                      {getProofImageUrl(c.proof_image) ? (
                        <a href={getProofImageUrl(c.proof_image)} target="_blank" rel="noreferrer" className="inline-block">
                          <img src={getProofImageUrl(c.proof_image)} alt="Payment proof" className="h-12 w-12 rounded-lg object-cover border border-gray-200 dark:border-gray-600 hover:opacity-80" />
                        </a>
                      ) : (
                        <span className="text-sm text-gray-400 dark:text-gray-500">-</span>
                      )}
                    </td>
                    <td className="p-4 text-right font-bold text-gray-900 dark:text-gray-100">
                      PKR {parseFloat(c.amount_collected).toLocaleString()}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination Controls */}
        {totalCount > 0 && (
          <div className="px-4 py-3 border-t border-gray-100 dark:border-gray-700 flex flex-col sm:flex-row items-center justify-between gap-3">
            <div className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400">
              <span>Per page:</span>
              <select
                value={pageSize}
                onChange={(e) => setPageSize(Number(e.target.value))}
                className="border border-gray-300 dark:border-gray-600 rounded px-2 py-1 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
              >
                {PAGE_SIZE_OPTIONS.map(n => <option key={n} value={n}>{n}</option>)}
              </select>
              <span className="ml-2">Showing {startItem}–{endItem} of {totalCount}</span>
            </div>

            <div className="flex items-center gap-1">
              <button onClick={() => goToPage(1)} disabled={currentPage === 1}
                className="p-1.5 rounded hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-30 disabled:cursor-not-allowed text-gray-600 dark:text-gray-300">
                <ChevronsLeft size={16} />
              </button>
              <button onClick={() => goToPage(currentPage - 1)} disabled={currentPage === 1}
                className="p-1.5 rounded hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-30 disabled:cursor-not-allowed text-gray-600 dark:text-gray-300">
                <ChevronLeft size={16} />
              </button>
              <span className="px-3 py-1 text-sm text-gray-700 dark:text-gray-200">
                {currentPage} / {totalPages}
              </span>
              <button onClick={() => goToPage(currentPage + 1)} disabled={currentPage === totalPages}
                className="p-1.5 rounded hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-30 disabled:cursor-not-allowed text-gray-600 dark:text-gray-300">
                <ChevronRight size={16} />
              </button>
              <button onClick={() => goToPage(totalPages)} disabled={currentPage === totalPages}
                className="p-1.5 rounded hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-30 disabled:cursor-not-allowed text-gray-600 dark:text-gray-300">
                <ChevronsRight size={16} />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CollectionHistory;
